import type { JSX } from 'preact';
import { useState } from 'preact/hooks';
import { T } from '../tokens';
import { useAppRouter } from '../router';
import type { RouterApi, ScreenId, ScreenParams, StackFrame, TabId } from '../router';
import { BottomNav } from './BottomNav';
import { Placeholder } from './Placeholder';
import { FeedScreen } from '../features/feed';
import { TreeScreen } from '../features/tree';
import { HistoryScreen, useChatHistory } from '../features/chat';
import type { useChatHistory as UseChatHistoryType } from '../features/chat';
import { useVoiceDock, VoiceDock } from '../features/voicedock';
import type { VoiceDockController } from '../features/voicedock';
import { ItemCard, EquipmentCard } from '../features/card';
import { SearchScreen } from '../features/search';
import { AddPickerSheet } from '../features/addpicker';
import type { AddKind } from '../features/addpicker';
import {
  AddItemScreen,
  AddRoomScreen,
  EditItemScreen,
  EditEquipmentScreen,
  EditRoomScreen,
  EditPropertyScreen,
  NewPropertyScreen,
} from '../features/forms';
import { DestructCascadeScreen } from '../features/destruct';
import { CalendarScreen } from '../features/calendar';
import { NotifsScreen } from '../features/notifs';
import { SeasonalScreen } from '../features/seasonal';
import { RegulationDiffScreen } from '../features/regulation';
import { CatalogEquipmentScreen, OutOfCatalogScreen } from '../features/equipment-add';
import { JitOnboardingScreen, WalkthroughScreen } from '../features/onboarding';
import { VideosScreen } from '../features/videos';
import { ProfileScreen } from '../features/profile';
import { UndoScreen } from '../features/undo';
import { ChangesScreen } from '../features/changes';
import { FeedbackButton, FeedbackCard } from '../features/feedback';

function paramString(params: Record<string, unknown>, key: string): string {
  const v = params[key];
  return typeof v === 'string' ? v : '';
}

type ChatApi = ReturnType<typeof UseChatHistoryType>;

const TAB_ISSUE_MAP: Partial<Record<TabId, string>> = {
  notifs: 'hh-78o',
};

const SCREEN_ISSUE_MAP: Partial<Record<ScreenId, string>> = {
  destruct_bulk: 'hh-78y',
};

export function AppShell(): JSX.Element {
  const router = useAppRouter();
  const chat = useChatHistory();
  const dock = useVoiceDock({ chat, router });
  const [feedbackOpen, setFeedbackOpen] = useState(false);

  const content = router.top
    ? renderStackFrame(router.top, router, dock)
    : renderTab(router.tab, router.tabParams, router, chat, dock);

  // Имя текущего экрана — уходит вместе с обращением обратной связи (CASE-48).
  const currentScreen = router.top !== null ? router.top.screen : router.tab;

  return (
    <>
      <div style={containerStyle}>
        <div style={contentStyle}>{content}</div>
        <VoiceDock dock={dock} />
        {router.top === null && (
          <BottomNav
            tab={router.tab}
            onTabChange={(next) => router.go(next)}
            onOpenVoice={dock.open}
          />
        )}
        {!feedbackOpen && <FeedbackButton onClick={() => setFeedbackOpen(true)} />}
      </div>

      {router.overlay === 'add_picker' && (
        <AddPickerSheet
          onClose={() => router.closeOverlay()}
          onPick={(kind: AddKind) => {
            router.closeOverlay();
            const screen =
              kind === 'item'
                ? 'add_item'
                : kind === 'room'
                  ? 'add_room'
                  : kind === 'equipment'
                    ? 'catalog_eq'
                    : 'new_property';
            router.go(screen);
          }}
        />
      )}

      {feedbackOpen && (
        <FeedbackCard screen={currentScreen} onClose={() => setFeedbackOpen(false)} />
      )}
    </>
  );
}

function renderTab(
  tab: TabId,
  tabParams: ScreenParams,
  router: RouterApi,
  chat: ChatApi,
  dock: VoiceDockController,
): JSX.Element {
  if (tab === 'feed') return <FeedScreen router={router} chat={chat} />;
  if (tab === 'tree') return <TreeScreen router={router} />;
  if (tab === 'calendar') return <CalendarScreen router={router} params={tabParams} />;
  if (tab === 'notifs') return <NotifsScreen router={router} />;
  if (tab === 'profile') return <ProfileScreen router={router} />;
  if (tab === 'search') {
    return <SearchScreen router={router} onOpenVoice={dock.open} />;
  }
  if (tab === 'history') {
    return (
      <HistoryScreen
        feed={chat.feed}
        onSendText={chat.sendText}
        onUndoAction={(changeId) => router.go('undo', { id: changeId })}
        onClarifyPick={(msgId, option) => void chat.pickClarifyOption(msgId, option)}
        onOpenTree={() => router.go('tree')}
      />
    );
  }
  const issueId = TAB_ISSUE_MAP[tab];
  return <Placeholder name={`tab:${tab}`} {...(issueId !== undefined ? { issueId } : {})} />;
}

function renderStackFrame(
  frame: StackFrame,
  router: RouterApi,
  dock: VoiceDockController,
): JSX.Element {
  switch (frame.screen) {
    case 'item_card':
      return <ItemCard router={router} itemId={paramString(frame.params, 'id')} />;
    case 'eq_card':
      return <EquipmentCard router={router} equipmentId={paramString(frame.params, 'id')} />;
    case 'add_item':
      return <AddItemScreen router={router} />;
    case 'add_room':
      return <AddRoomScreen router={router} />;
    case 'edit_item':
      return <EditItemScreen router={router} itemId={paramString(frame.params, 'id')} />;
    case 'edit_eq':
      return <EditEquipmentScreen router={router} equipmentId={paramString(frame.params, 'id')} />;
    case 'edit_room':
      return <EditRoomScreen router={router} roomId={paramString(frame.params, 'id')} />;
    case 'edit_property':
      return <EditPropertyScreen router={router} propertyId={paramString(frame.params, 'id')} />;
    case 'destruct_cascade':
      return <DestructCascadeScreen router={router} propertyId={paramString(frame.params, 'id')} />;
    case 'new_property':
      return <NewPropertyScreen router={router} />;
    case 'catalog_eq':
      return <CatalogEquipmentScreen router={router} />;
    case 'out_of_catalog':
      return <OutOfCatalogScreen router={router} />;
    case 'season':
      return <SeasonalScreen router={router} />;
    case 'jit':
      return <JitOnboardingScreen router={router} onOpenVoice={dock.open} />;
    case 'walkthrough':
      return <WalkthroughScreen router={router} />;
    case 'videos':
      return <VideosScreen router={router} />;
    case 'regulation':
      return <RegulationDiffScreen router={router} equipmentId={paramString(frame.params, 'id')} />;
    case 'undo':
      return <UndoScreen router={router} />;
    case 'changes':
      return <ChangesScreen router={router} />;
    default: {
      const issueId = SCREEN_ISSUE_MAP[frame.screen];
      return (
        <Placeholder
          name={`screen:${frame.screen}`}
          onBack={router.back}
          {...(issueId !== undefined ? { issueId } : {})}
        />
      );
    }
  }
}

const containerStyle: JSX.CSSProperties = {
  position: 'absolute',
  inset: 0,
  display: 'flex',
  flexDirection: 'column',
  background: T.bg,
};

const contentStyle: JSX.CSSProperties = {
  flex: 1,
  minHeight: 0,
  position: 'relative',
  overflow: 'hidden',
};
